import { ScrollArea } from "@volca/ui/components/ui/scroll-area";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import {
  AssigneeSelect,
  DetailPanelHeader,
  DetailSidebarFallback,
  ReviewBadge,
  StatusBadge,
} from "@/accounts-payable/common";
import { useGetInvoice } from "@/accounts-payable/query";
import {
  formatApDate,
  invoiceStatusMeta,
  reviewStateMeta,
} from "@/accounts-payable/utils";
import { formatCentsOrDashIfZero } from "@/core/utils/money";
import { InvoiceDetailBody } from "./invoice-detail-body";

export const ApInvoiceDetail = ({
  invoiceId,
  businessId,
  onClose,
  onViewAgentReview,
}: {
  invoiceId: string;
  businessId: string | null;
  onClose: () => void;
  onViewAgentReview?: (invoiceId: string) => void;
}) => {
  const { data: invoice, isPending } = useGetInvoice(businessId, invoiceId);

  if (!invoice) {
    return (
      <DetailSidebarFallback
        title="Invoice"
        loading={isPending}
        errorMessage="Couldn’t load the invoice. Please try again."
        onClose={onClose}
      />
    );
  }

  return (
    <Stack align="start" gap={0} className="h-full w-full">
      {/* Same tiers as the review page's header: what it is, then where
          it stands. */}
      <DetailPanelHeader
        title={invoice.vendor_name}
        facts={[
          invoice.invoice_number,
          invoice.invoice_date && formatApDate(invoice.invoice_date),
          invoice.total_amount_cents ? (
            <span
              className={cn("font-semibold tabular-nums", {
                "text-danger": invoice.total_amount_cents < 0,
                "text-ink": invoice.total_amount_cents >= 0,
              })}
            >
              {formatCentsOrDashIfZero(invoice.total_amount_cents)}
            </span>
          ) : null,
        ]}
        statuses={
          <>
            <StatusBadge meta={invoiceStatusMeta(invoice)} />
            {reviewStateMeta(invoice.review_state) && (
              <ReviewBadge document={invoice} />
            )}
          </>
        }
        aside={
          <AssigneeSelect document={invoice} businessId={businessId} compact />
        }
        onClose={onClose}
      />

      {/* Body */}
      <ScrollArea className="min-h-0 w-full flex-1">
        <InvoiceDetailBody
          invoice={invoice}
          businessId={businessId}
          onViewAgentReview={(id) => {
            onClose();
            onViewAgentReview?.(id);
          }}
        />
      </ScrollArea>
    </Stack>
  );
};
